import { config } from '#~/config/config.js';

/**
 * Fire `onReachable` once, as soon as the cloud API answers again.
 *
 * Why: a boot that fails because the WAN is down (registry reads time out)
 * ends on a retryable boot error, and nothing retried it. With the LAN up,
 * `navigator.onLine` stays true and no `online` event ever fires, so after the
 * uplink came back Studio sat on "Something went wrong" / "Loading your
 * studio" until someone clicked Try Again. This polls a cheap
 * probe and hands control back to the caller's own retry path when it passes.
 *
 * The default probe is a CORS HEAD to the API origin: it resolves on any HTTP
 * status the API itself sends (403 at the root) and rejects when the host
 * cannot be reached at all, which is the one question the caller needs answered.
 *
 * Per rule: think-in-systems + reference-rules-in-comments (ai.knowledge/global).
 *
 * @returns a disposer that stops polling (call it on unmount / supersede).
 */
export function retryWhenReachable(
  onReachable: () => void,
  {
    probe = probeApi,
    intervalMs = 10_000
  }: { probe?: () => Promise<boolean>; intervalMs?: number } = {}
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const tick = (): void => {
    timer = setTimeout(() => {
      void probe()
        .catch(() => false)
        .then((ok) => {
          if (stopped) return;
          if (ok) {
            stopped = true;
            onReachable();
          } else tick();
        });
    }, intervalMs);
  };
  tick();
  return () => {
    stopped = true;
    clearTimeout(timer);
  };
}

/** One reachability probe of the cloud API (the default `retryWhenReachable` probe). */
export const isApiReachable = (): Promise<boolean> => probeApi().catch(() => false);

const probeApi = async (): Promise<boolean> => {
  const endpoint = config.apiEndpoint;
  if (!endpoint) return false;
  // A black-holed WAN never answers; bound each probe so they cannot pile up.
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), 5_000);
  try {
    // A captive portal hijacks DNS and 302s every request to its login page,
    // and the desktop app runs with --ignore-certificate-errors, so a TLS
    // mismatch does not reject it. The API origin itself answers without a
    // redirect (403 at the root, with CORS headers), so a redirect means "not
    // the cloud yet". Must be `cors`: Fetch rejects `no-cors` + `redirect:
    // 'manual'` outright, which made every probe fail on YEET. `cors` also
    // rejects a portal page that answers 200 without the API's CORS headers.
    const response = await fetch(endpoint, {
      method: 'HEAD',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      redirect: 'manual',
      signal: abort.signal
    });
    return response.type !== 'opaqueredirect';
  } finally {
    clearTimeout(timer);
  }
};
